/**
 * DrumLeaderboard – top scores for a single drum challenge.
 *
 * Kept separate from the track leaderboard: that page is organised around
 * selecting a track, while this one selects an instrument and then one of its
 * ten challenges. Public, like the track leaderboard.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AFRICAN_INSTRUMENTS } from '../../../data/africanInstruments';
import { usePageLoadAnimation } from '../../../hooks/useAnimation';
import { useAuth } from '../../../context/AuthContext';
import { getChallenge, getChallenges } from '../data/challenges';
import { DrumScoreEntry, fetchDrumLeaderboard } from '../api';
import {
  GRADE_COLORS,
  NEUTRAL,
  RANK_COLORS,
  accuracyColor,
  formatDateShort,
} from '../lib/theme';

export const DrumLeaderboard: React.FC = () => {
  usePageLoadAnimation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // A ?challenge= link (from a finished run) decides the initial selection.
  const requested = searchParams.get('challenge');
  const requestedChallenge = requested ? getChallenge(requested) : undefined;

  const [instrumentSlug, setInstrumentSlug] = useState(
    requestedChallenge?.instrumentSlug ?? AFRICAN_INSTRUMENTS[0].slug,
  );
  const [challengeId, setChallengeId] = useState(
    requestedChallenge?.id ?? `${AFRICAN_INSTRUMENTS[0].slug}-1`,
  );
  const [entries, setEntries] = useState<DrumScoreEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const challenges = useMemo(
    () => getChallenges(instrumentSlug),
    [instrumentSlug],
  );
  const challenge = getChallenge(challengeId);

  const loadBoard = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await fetchDrumLeaderboard(id));
    } catch (e) {
      setError((e as Error).message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBoard(challengeId);
  }, [challengeId, loadBoard]);

  const selectInstrument = (slug: string) => {
    setInstrumentSlug(slug);
    // Jump to that instrument's first challenge rather than keeping an index
    // that belongs to a different drum.
    const first = `${slug}-1`;
    setChallengeId(first);
    setSearchParams({ challenge: first }, { replace: true });
  };

  const selectChallenge = (id: string) => {
    setChallengeId(id);
    setSearchParams({ challenge: id }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-studio-950">
      <section className="relative pt-24 pb-10 px-4 sm:px-8 lg:px-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
        <div className="max-w-7xl mx-auto relative text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3 block">
            Drum Rankings
          </span>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-stone-100 mb-3">
            Drum <span className="italic text-amber-500">Leaderboard</span>
          </h1>
          <div className="flex items-center justify-center gap-6 mt-5 text-sm">
            <Link to="/drum-challenges" className="text-stone-500 hover:text-amber-500 transition-colors">
              Play Challenges
            </Link>
            <Link to="/drum-soundboard" className="text-stone-500 hover:text-amber-500 transition-colors">
              Soundboard
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 pb-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          {/* Selectors */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-studio-900/50 border border-studio-800 rounded-2xl p-5">
              <h2 className="text-stone-100 font-semibold mb-3">Instrument</h2>
              <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                {AFRICAN_INSTRUMENTS.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => selectInstrument(item.slug)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      item.slug === instrumentSlug
                        ? 'bg-amber-500/20 text-amber-500 font-semibold'
                        : 'text-stone-400 hover:bg-studio-800/60 hover:text-stone-200'
                    }`}
                  >
                    {item.nameEn}
                    <span className="text-stone-600 ml-2">{item.nameZh}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-studio-900/50 border border-studio-800 rounded-2xl p-5">
              <h2 className="text-stone-100 font-semibold mb-3">Challenge</h2>
              <div className="grid grid-cols-5 gap-2">
                {challenges.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectChallenge(item.id)}
                    title={item.name}
                    className={`aspect-square rounded-lg text-sm font-bold transition-all ${
                      item.id === challengeId
                        ? 'bg-amber-500 text-studio-950'
                        : 'bg-studio-800 text-stone-400 hover:bg-studio-700 hover:text-stone-200'
                    }`}
                  >
                    {item.index}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Board */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-r from-studio-900/80 to-studio-900/40 border border-studio-800 rounded-2xl p-6 mb-6">
              <h2 className="text-2xl font-bold text-stone-100 mb-1">
                {challenge ? `${challenge.index}. ${challenge.name}` : 'Challenge'}
              </h2>
              <p className="text-stone-500 font-mono text-sm">
                {challenge
                  ? `${challenge.bpm} BPM · ${challenge.steps.length} strokes`
                  : ''}
              </p>
            </div>

            <div className="bg-studio-900/30 border border-studio-800 rounded-2xl overflow-hidden">
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <p className="text-coral-400 mb-4">{error}</p>
                  <button
                    type="button"
                    onClick={() => void loadBoard(challengeId)}
                    className="btn-secondary"
                  >
                    Try Again
                  </button>
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-stone-500 text-lg mb-2">No scores yet</p>
                  <p className="text-stone-600 text-sm mb-5">
                    Be the first to set a record on this challenge.
                  </p>
                  <Link to="/drum-challenges" className="btn-primary">
                    Play it
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-studio-800 bg-studio-900/50 text-stone-500 text-xs font-semibold uppercase tracking-wider">
                        <th className="text-left py-4 px-4 sm:px-6 w-16">Rank</th>
                        <th className="text-left py-4 px-4 sm:px-6">Player</th>
                        <th className="text-right py-4 px-4 sm:px-6">Score</th>
                        <th className="text-center py-4 px-4 sm:px-6 hidden sm:table-cell">
                          Grade
                        </th>
                        <th className="text-right py-4 px-4 sm:px-6 hidden md:table-cell">
                          Accuracy
                        </th>
                        <th className="text-right py-4 px-4 sm:px-6 hidden lg:table-cell">
                          Combo
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, index) => {
                        const rank = index + 1;
                        const rankColor = RANK_COLORS[rank] ?? NEUTRAL;
                        const gradeColor = GRADE_COLORS[entry.grade] ?? NEUTRAL;
                        const isMe =
                          !!entry.username && entry.username === user?.username;

                        return (
                          <tr
                            key={entry.id}
                            className={`border-b border-studio-800/50 transition-colors hover:bg-studio-800/30 ${
                              isMe ? 'bg-amber-500/5' : ''
                            }`}
                          >
                            <td className="py-4 px-4 sm:px-6">
                              <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-lg font-bold"
                                style={{
                                  backgroundColor:
                                    rank <= 3 ? `${rankColor}20` : 'transparent',
                                  color: rankColor,
                                  border:
                                    rank <= 3
                                      ? `2px solid ${rankColor}40`
                                      : '2px solid transparent',
                                }}
                              >
                                {rank}
                              </div>
                            </td>
                            <td className="py-4 px-4 sm:px-6">
                              <p
                                className={`font-semibold ${
                                  isMe ? 'text-amber-500' : 'text-stone-100'
                                }`}
                              >
                                {entry.username ?? 'Unknown player'}
                                {isMe && (
                                  <span className="ml-2 text-xs text-amber-500/70">
                                    (You)
                                  </span>
                                )}
                              </p>
                              <p className="text-stone-600 text-xs">
                                {formatDateShort(entry.created_at)}
                              </p>
                            </td>
                            <td className="py-4 px-4 sm:px-6 text-right">
                              <span className="font-display text-xl font-bold text-stone-100">
                                {entry.score.toLocaleString()}
                              </span>
                            </td>
                            <td className="py-4 px-4 sm:px-6 text-center hidden sm:table-cell">
                              <span
                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg font-display text-lg font-bold"
                                style={{
                                  backgroundColor: `${gradeColor}20`,
                                  color: gradeColor,
                                  border: `2px solid ${gradeColor}40`,
                                }}
                              >
                                {entry.grade}
                              </span>
                            </td>
                            <td className="py-4 px-4 sm:px-6 text-right hidden md:table-cell">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-studio-800 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${entry.accuracy}%`,
                                      backgroundColor: accuracyColor(entry.accuracy),
                                    }}
                                  />
                                </div>
                                <span className="font-mono text-sm text-stone-300 w-14">
                                  {entry.accuracy.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-4 sm:px-6 text-right hidden lg:table-cell">
                              <span className="font-mono text-stone-300">
                                {entry.max_combo.toLocaleString()}
                                <span className="text-stone-600 text-xs ml-1">x</span>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
